'use client';

import * as React from 'react';

import { Button } from '@workspace/ui/components/button';
import { Input } from '@workspace/ui/components/input';
import { toast } from '@workspace/ui/components/sonner';

import { subscribeNewsletter } from '~/actions/subscribe-newsletter';

export function Newsletter(): React.JSX.Element {
  const [email, setEmail] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [emailError, setEmailError] = React.useState<string>();

  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) {
      return 'E-mailadres is verplicht.';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return 'Voer een geldig e-mailadres in.';
    }
    if (email.length > 254) {
      return 'E-mailadres mag maximaal 254 karakters bevatten.';
    }
    return undefined;
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();

    // Clear previous errors
    setEmailError(undefined);

    // Validate email
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await subscribeNewsletter({ email: email.trim() });

      if (result?.serverError) {
        toast.error(result.serverError);
        return;
      }

      if (result?.validationErrors?.email) {
        setEmailError(
          result.validationErrors.email._errors?.[0] ?? 'Ongeldige e-mail.'
        );
        return;
      }

      // Success
      toast.success('Je bent succesvol ingeschreven voor onze nieuwsbrief!');
      setEmail('');
    } catch {
      toast.error('Er ging iets mis. Probeer het later opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(e.target.value);
    // Clear error when user starts typing
    if (emailError) {
      setEmailError(undefined);
    }
  };

  return (
    <div className="mt-10 space-y-4 lg:col-span-2 xl:mt-0">
      <h3 className="text-sm font-semibold text-foreground">
        Schrijf je in voor onze nieuwsbrief
      </h3>
      <form
        onSubmit={handleSubmit}
        className="py-2 sm:flex sm:max-w-md"
      >
        <div className="w-full min-w-0">
          <Input
            type="email"
            placeholder="Voer je e-mailadres in"
            className="w-full"
            value={email}
            onChange={handleEmailChange}
            disabled={isSubmitting}
            aria-invalid={!!emailError}
            aria-describedby={emailError ? 'email-error' : undefined}
          />
          {emailError && (
            <p
              id="email-error"
              className="mt-1 text-sm text-destructive"
            >
              {emailError}
            </p>
          )}
        </div>
        <div className="mt-3 sm:ml-4 sm:mt-0 sm:shrink-0">
          <Button
            type="submit"
            disabled={isSubmitting || !email.trim()}
          >
            {isSubmitting ? 'Bezig...' : 'Inschrijven'}
          </Button>
        </div>
      </form>
    </div>
  );
}
